import React, { useState } from "react";
import {
  useShippingProfiles,
  useCreateShippingProfile,
  useDeleteShippingProfile,
  useUpdateShippingProfile,
  useSetDefaultShippingProfile,
  useReference,
} from "@/lib/queries";
import { toast } from "sonner";
import { Home, Plus, Trash2, Star, ShieldCheck, MapPin, Pencil, X } from "lucide-react";

const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]";
const LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";

const EMPTY = {
  label: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address1: "",
  address2: "",
  city: "",
  region: "",
  postal: "",
  country: "US",
  is_default: false,
};

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "JP", name: "Japan" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
];


export default function ShippingProfilesPage() {
  const { data: items = [], isLoading } = useShippingProfiles();
  const create = useCreateShippingProfile();
  const update = useUpdateShippingProfile();
  const del = useDeleteShippingProfile();
  const setDefault = useSetDefaultShippingProfile();
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const startEdit = (it) => {
    setEditingId(it.id);
    setShowForm(true);
    // PII fields (phone / addresses) are encrypted at rest and the API
    // doesn't return decrypted values. We open the form with blanks for
    // those fields — the backend treats blanks as "keep existing" so the
    // user can edit only what they want without erasing the rest.
    setForm({
      label: it.label || "",
      first_name: it.first_name || "",
      last_name: it.last_name || "",
      email: "",
      phone: "",
      address1: "",
      address2: "",
      city: it.city || "",
      region: it.region || "",
      postal: it.postal || "",
      country: it.country || "US",
      is_default: !!it.is_default,
    });
    setTimeout(() => document.querySelector('[data-testid="shipping-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
    setShowForm(false);
  };

  const submit = (e) => {
    e.preventDefault();
    const onOk = () => {
      toast.success(editingId ? "Address updated" : "Shipping profile saved", {
        description: "Encrypted at rest — only used for guest checkout in Scalp mode",
      });
      cancelEdit();
    };
    const onErr = (err) => toast.error("Failed", { description: err?.response?.data?.detail || err.message });
    if (editingId) {
      update.mutate({ id: editingId, body: form }, { onSuccess: onOk, onError: onErr });
    } else {
      create.mutate(form, { onSuccess: onOk, onError: onErr });
    }
  };

  return (
    <div className="p-6 max-w-4xl w-full mx-auto" data-testid="shipping-page">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
            Address book
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Shipping profiles for <span className="text-[#EAB308]">guest checkout</span> in Scalp mode — no retailer account required. All fields encrypted at rest.
          </p>
        </div>
        <button
          data-testid="show-shipping-form"
          onClick={() => setShowForm((s) => !s)}
          className="bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-sm hover:bg-[#FDE047] flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Close" : "Add address"}
        </button>
      </header>

      {/* Why this exists */}
      <div className="bg-[#22C55E]/5 border border-[#22C55E]/30 p-3 rounded-sm mb-6 flex gap-3" data-testid="shipping-howto">
        <ShieldCheck className="w-5 h-5 text-[#22C55E] shrink-0 mt-0.5" />
        <div className="text-[12px] leading-relaxed text-[#D4D4D4]">
          <strong className="text-white">What this is for:</strong> When a Smart Action&apos;s Auto-buy is set to <span className="text-[#EAB308]">guest checkout mode</span>, the bot skips the retailer login and fills the &ldquo;Continue as Guest&rdquo; form using one of these saved profiles. Faster on drops where login servers are crushed. Combine with a Payment Method (still required) to complete the order.
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#0d0d0d] border border-[#262626] rounded-sm p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="shipping-form">
          <div className="md:col-span-2 flex items-center justify-between border-b border-[#262626] pb-2 mb-1">
            <div className="font-heading text-sm uppercase tracking-wider text-white flex items-center gap-2">
              {editingId ? <><Pencil className="w-4 h-4 text-[#3B82F6]" /> Edit address</> : <><Plus className="w-4 h-4 text-[#EAB308]" /> New address</>}
            </div>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="text-[#737373] hover:text-white" title="Cancel edit"><X className="w-4 h-4" /></button>
            )}
          </div>
          {editingId && (
            <div className="md:col-span-2 bg-[#3B82F6]/5 border border-[#3B82F6]/30 rounded-sm p-2.5 text-[11px] text-[#93C5FD] leading-relaxed" data-testid="shipping-edit-hint">
              <strong className="text-white">Editing encrypted address.</strong> Fields below are <span className="text-[#3B82F6]">blank by design</span> — your saved values are encrypted at rest and can&apos;t be re-shown. Only fill in what you want to change; everything else stays as it was.
            </div>
          )}
          <div className="md:col-span-2">
            <div className={LABEL}>Label *</div>
            <input data-testid="ship-label" className={INPUT} placeholder="Home, Office, Mum's place…" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </div>
          <div>
            <div className={LABEL}>First name {editingId ? "" : "*"}</div>
            <input data-testid="ship-first-name" className={INPUT} required={!editingId} placeholder={editingId ? "(unchanged)" : ""} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div>
            <div className={LABEL}>Last name {editingId ? "" : "*"}</div>
            <input data-testid="ship-last-name" className={INPUT} required={!editingId} placeholder={editingId ? "(unchanged)" : ""} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <div>
            <div className={LABEL}>Email {editingId ? "" : "*"}</div>
            <input data-testid="ship-email" type="email" className={INPUT} required={!editingId} placeholder={editingId ? "(unchanged)" : ""} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <div className={LABEL}>Phone {editingId ? "" : "*"}</div>
            <input data-testid="ship-phone" className={INPUT} required={!editingId} placeholder={editingId ? "(unchanged)" : ""} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <div className={LABEL}>Address line 1 {editingId ? "" : "*"}</div>
            <input data-testid="ship-address1" className={INPUT} required={!editingId} placeholder={editingId ? "(unchanged)" : "123 Card Lane"} value={form.address1} onChange={(e) => setForm({ ...form, address1: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <div className={LABEL}>Address line 2</div>
            <input data-testid="ship-address2" className={INPUT} placeholder={editingId ? "(unchanged — leave blank to keep current)" : "Apt, suite, etc. (optional)"} value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })} />
          </div>
          <div>
            <div className={LABEL}>City {editingId ? "" : "*"}</div>
            <input data-testid="ship-city" className={INPUT} required={!editingId} placeholder={editingId ? "(unchanged)" : ""} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div>
            <div className={LABEL}>State / Region {editingId ? "" : "*"}</div>
            <input data-testid="ship-region" className={INPUT} required={!editingId} placeholder={editingId ? "(unchanged)" : "OR, NSW, QC…"} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
          </div>
          <div>
            <div className={LABEL}>Postal code {editingId ? "" : "*"}</div>
            <input data-testid="ship-postal" className={INPUT} required={!editingId} placeholder={editingId ? "(unchanged)" : ""} value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value })} />
          </div>
          <div>
            <div className={LABEL}>Country</div>
            <select data-testid="ship-country" className={INPUT} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 md:col-span-2 text-[12px] text-[#D4D4D4] cursor-pointer">
            <input type="checkbox" data-testid="ship-is-default" className="accent-[#EAB308]" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
            Set as default for guest checkout
          </label>
          <div className="md:col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={cancelEdit} className="px-4 py-2 border border-[#262626] text-[#A3A3A3] uppercase tracking-wider text-xs font-bold rounded-sm hover:bg-[#1A1A1A]">Cancel</button>
            <button type="submit" data-testid="ship-save" disabled={create.isPending || update.isPending} className="px-4 py-2 bg-[#EAB308] text-black uppercase tracking-wider text-xs font-bold rounded-sm hover:bg-[#FDE047] disabled:opacity-40">{editingId ? "Save changes" : "Save address"}</button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="shipping-list">
        {isLoading && <div className="text-[#737373] text-sm">Loading…</div>}
        {!isLoading && items.length === 0 && (
          <div className="md:col-span-2 text-[#737373] text-sm py-8 text-center border border-dashed border-[#262626] rounded-sm">
            No shipping profiles yet. Add one above so Scalp mode can run guest checkouts.
          </div>
        )}
        {items.map((s) => (
          <div
            key={s.id}
            data-testid={`shipping-card-${s.id}`}
            className={"bg-[#0d0d0d] border rounded-sm p-4 " + (s.is_default ? "border-[#EAB308]/60" : "border-[#262626]")}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-[#EAB308]" />
                <div className="font-heading text-base font-bold text-white">{s.label}</div>
                {s.is_default && (
                  <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#EAB308] border border-[#EAB308]/40 px-1.5 py-0.5 rounded-sm">DEFAULT</span>
                )}
              </div>
              <div className="flex gap-1">
                {!s.is_default && (
                  <button
                    data-testid={`ship-default-${s.id}`}
                    onClick={() => setDefault.mutate(s.id, { onSuccess: () => toast.success("Default set") })}
                    title="Set as default"
                    className="text-[#737373] hover:text-[#EAB308]"
                  ><Star className="w-3.5 h-3.5" /></button>
                )}
                <button
                  data-testid={`ship-edit-${s.id}`}
                  onClick={() => startEdit(s)}
                  title="Edit this address"
                  className="text-[#737373] hover:text-[#3B82F6]"
                ><Pencil className="w-3.5 h-3.5" /></button>
                <button
                  data-testid={`ship-delete-${s.id}`}
                  onClick={() => { if (confirm(`Delete "${s.label}"?`)) del.mutate(s.id, { onSuccess: () => toast.success("Deleted") }); }}
                  className="text-[#737373] hover:text-[#EF4444]"
                ><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="text-[13px] text-white">{s.first_name} {s.last_name}</div>
            <div className="text-[11px] text-[#A3A3A3] font-mono">{s.email_masked}</div>
            <div className="flex items-start gap-1.5 mt-2 text-[12px] text-[#D4D4D4]">
              <MapPin className="w-3.5 h-3.5 text-[#737373] shrink-0 mt-0.5" />
              <div>{s.city}, {s.region} {s.postal} · {s.country}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
